import React, { useContext, useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import { useNavigate } from 'react-router-dom';
import { useAppTrackCheckIns } from '../../../hooks/useAppTrackCheckIn';
import { AppTrackCheckIn } from '../../../models/AppTrackCheckIn';
import { Heading } from '../../../components/Heading';
import Loading from '../../../components/Loading';
import { EyeIcon } from '@heroicons/react/16/solid';
import Pagination from '../../../components/Pagination';
import dateFormat from 'dateformat';
import { Text } from '../../../components/Text';
import { useAppUser } from '../../../hooks/useAppUser';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { UserContext } from '../../../context/userContext';
import { useWarehouses } from '../../../hooks/useWarehouse';

const COLUMNS = [
    { key: 'salesman', label: 'Salesman' },
    { key: 'location', label: 'Location' },
    { key: 'time', label: 'Time' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const AppTrackCheckInPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const { loading, error, fetchTrackCheckInsPaginated } = useAppTrackCheckIns();
    const { warehouses } = useWarehouses();
    const [trackCheckIns, setTrackCheckIns] = useState<AppTrackCheckIn[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterFromDate, setFilterFromDate] = useState('');
    const [filterToDate, setFilterToDate] = useState('');
    const [filterSalesman, setFilterSalesman] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const perPage = 10;
    const navigate = useNavigate();
    const { appUsers } = useAppUser();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));

    useEffect(() => {
        document.title = "Track Check-Ins";
    }, []);

    useEffect(() => {
        (async () => {
            const res = await fetchTrackCheckInsPaginated(currentPage, perPage, searchQuery || undefined, filterSalesman || undefined, filterFromDate || undefined, filterToDate || undefined, filterWarehouseId || undefined);
            if (res) {
                setTrackCheckIns(res.checkins);
                setTotal(res.total);
            }
        })();
    }, [currentPage, searchQuery, filterSalesman, filterFromDate, filterToDate, filterWarehouseId, fetchTrackCheckInsPaginated]);

    const handleReset = () => {
        setFilterFromDate('');
        setFilterToDate('');
        setFilterSalesman('');
        setFilterWarehouseId('');
        setSearchQuery('');
        setCurrentPage(1);
    };

    const activeFilterCount = [filterFromDate, filterToDate, filterSalesman, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterFromDate ? [{ key: 'from', label: `From: ${filterFromDate}`, onClear: () => setFilterFromDate('') }] : []),
        ...(filterToDate ? [{ key: 'to', label: `To: ${filterToDate}`, onClear: () => setFilterToDate('') }] : []),
        ...(filterSalesman ? [{
            key: 'salesman',
            label: `Salesman: ${appUsers?.find((u) => u.id?.toString() === filterSalesman)?.username || filterSalesman}`,
            onClear: () => setFilterSalesman(''),
        }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    const filteredTrackCheckIns = [...trackCheckIns].sort((a, b) => {
        const diff = new Date(a.created_time).getTime() - new Date(b.created_time).getTime();
        return sortOrder === 'newest' ? -diff : diff;
    }); // server-filtered, client-sorted

    return (
        <div>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Check Ins</Heading>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search by salesman…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={handleReset} />

            <ul className="mt-6">
                {loading && <div className="w-full text-center h-full py-36 justify-center"><Loading /></div>}
                {error && <p className="py-16 text-center text-red-700">{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'salesman') && <TableHeader>Salesman</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'location') && <TableHeader>Location</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'time') && <TableHeader>Time</TableHeader>}
                                    <TableHeader className='text-center'>Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTrackCheckIns.length > 0 ? filteredTrackCheckIns.map((checkIn, index) => (
                                    <TableRow key={checkIn.id}>
                                        <TableCell>{(currentPage - 1) * perPage + index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'salesman') && <TableCell>{checkIn.salesman_username}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'location') && <TableCell>{checkIn.location_name}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'time') && <TableCell>{dateFormat(checkIn.created_time, "mmmm dd, yyyy hh:MM:ss")}</TableCell>}
                                        <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">
                                            <a
                                                onClick={() => navigate(`/gp/app/track/checkins/view/${checkIn.id}`)}
                                                className="text-zinc-600 hover:text-zinc-900 cursor-pointer"
                                            >
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8">No check in found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(total / perPage)}
                            onPageChange={setCurrentPage}
                            totalResult={total}
                            dataName={'Check Ins'}
                        />
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={handleReset}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterFromDate}
                        to={filterToDate}
                        onChange={({ from, to }) => { setFilterFromDate(from); setFilterToDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Salesman</Text>
                    <Select value={filterSalesman} onChange={(e) => setFilterSalesman(e.target.value)}>
                        <option value="">All Salesman</option>
                        {appUsers?.map((user) => (
                            <option key={user.id} value={user.id?.toString()}>
                                {user.username}
                            </option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role?.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>
        </div>
    );
};

export default AppTrackCheckInPage;